import {ActionIcon, Button, Paper, Select, Switch, TextInput} from "@mantine/core";
import {FloppyDiskIcon, UploadIcon, TrashIcon} from "@phosphor-icons/react";
import {useCallback, useContext, useEffect, useRef, useState} from "react";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import {Row, Col} from "../../../components/Flex/Flex";
import LoaderScreen from "../../../components/Loader/LoaderScreen";
import MessageContext from "../../../context/MessageContext";
import api from "../../../utils/api.ts";
import {shieldedStatus} from "../data.js";
import style from './Taverns.module.css';


const optShieldStatus = shieldedStatus.map(s => ({label: s, value: s}));

function ManageTaverns ({faction}) {
    const {success, error} = useContext(MessageContext);
    const [taverns, setTaverns] = useState([]);
    const [loading, setLoading] = useState('');
    const fileInputRef = useRef(null);

    const update = useCallback((id, key, value) => {
        setTaverns(prev => {
            let next = [...prev];
            let i = next.findIndex(el => el.id === id);
            let tavern = {...next[i]};
            tavern[key] = value;
            next[i] = tavern;
            return next;
        })
    }, []);

    const remove = useCallback(id => {
        setTaverns(prev => prev.filter(el => el.id !== id));
    }, []);

    const handleFileChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setLoading('Uploading and processing the file');
        const formData = new FormData();
        formData.append('taverns', file);

        fetch(import.meta.env.VITE_BACKEND_URL + "api/bh/manage/tavern/" + faction.id + "/upload", {
            method: 'POST',
            body: formData,
            credentials: 'include'
        }).then(res => {
            if (!res.ok) {
                return res.json().then(parsed => {
                    setLoading('');
                    if (parsed?.message) {
                        error('Upload failed', parsed.message);
                    } else {
                        error('Upload failed', 'Server responded with status ' + res.status + ' - ' + res.statusText);
                    }
                    throw new Error('Upload failed');
                });
            }
            return res.json();
        }).then(data => {
            setTaverns(data.parsed);
            setLoading('');
            success('XML loaded', data.added + ' taverns added, ' + data.updated + ' taverns updated');
            if (data.wrongOwner > 0) {
                error('Invalid owner', data.wrongOwner + ' facilities were ignored because they are not owned by this faction');
            }
            if (data.wrongType > 0) {
                error('Invalid facility type', data.wrongType + ' facilities were ignored because they are not taverns');
            }
        }).catch(() => {
            setLoading('');
        });

        // Reset file input so same file can be selected again
        e.target.value = '';
    }, [faction.id]);


    const save = () => {
        setLoading('Updating the tavern network');
        api.post('/bh/manage/taverns/'+faction.id, taverns).then(() => {
            success('Tavern network updated');
            setLoading('');
        });
    }

    useEffect(() => {
        setLoading('Loading tavern list');
        api.get('/bh/manage/taverns/'+faction.id).then(res => {
            setTaverns(res.data);
        }).finally(() => setLoading(''));
    }, [faction.id]);

    return (
        <div>
            <Paper className="mb-3">
                <p className="mt-0">
                    Tavern can be imported via xml. Filter your faction inventory to include only the taverns you want to add/update<br/>
                    If a tavern already exist its name and access status will be updated<br/>
                    Removal has to be done manually
                </p>
            </Paper>

            <Row>
                <Col grow={1}>
                    <h2 className="my-0">{faction.name} Taverns ({taverns.length})</h2>
                </Col>
                <Col>
                    <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="text/xml" style={{display: 'none'}} />
                    <Button leftSection={<UploadIcon size={16}/>} onClick={() => fileInputRef.current?.click()}>Upload XML</Button>
                </Col>

                <Col>
                    <Button leftSection={<FloppyDiskIcon size={16}/>} onClick={save}>Save</Button>
                </Col>
            </Row>
            {loading!=='' ? <LoaderScreen message={loading} /> : null}
            <Paper className="mt-2">
                <div className={style.wrapper}>
                    <table className={style.table}>
                        <thead>
                        <tr>
                            <th>Tavern</th>
                            <th>Planet</th>
                            <th>Location</th>
                            <th>Access</th>
                            <th>Enabled</th>
                            <th>Private</th>
                            <th>Shield Status</th>
                            <th>Notes</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {taverns?.map(t => <RowTavern key={t.id} tavern={t} update={update} remove={remove}/>)}
                        </tbody>
                    </table>
                </div>
            </Paper>
        </div>
    )
}


const RowTavern = ({tavern, update, remove}) => {
    return (
        <tr>
            <td>
                {tavern.name}<br/>
                <small>ID: {tavern.id}</small>
            </td>
            <td>
                {tavern.sector.name}<br/>
                {tavern.system.name} ({tavern.system.x}, {tavern.system.y})<br/>
                {tavern.planet.name} ({tavern.planet.x}, {tavern.system.y})<br/>
            </td>
            <td>
                City: {tavern.city.name} ({tavern.city.x}, {tavern.city.y})<br/>
                Ground location: ({tavern.x}, {tavern.y})
            </td>
            <td>{tavern.access}</td>
            <td><Switch checked={tavern.enabled} onChange={e => update(tavern.id, 'enabled', e.currentTarget.checked)}
                             title="Disabled tavern won't show up outside of this manager page"/></td>
            <td><Switch checked={tavern.private} onChange={e => update(tavern.id, 'private', e.currentTarget.checked)}
                             title="Private taverns will only show for members of the current faction or any faction that is on the allowlist"/></td>
            <td><Select data={optShieldStatus} value={tavern.shield} onChange={v => update(tavern.id, 'shield', v)}/></td>
            <td><TextInput value={tavern.notes} onChange={e => update(tavern.id, 'notes', e.target.value)}/></td>
            <td><ActionIcon onClick={() => remove(tavern.id)}><TrashIcon size={16}/></ActionIcon></td>
        </tr>
    )
}


export default withRouteErrorBoundary(ManageTaverns);
